import { Injectable, Optional } from '@angular/core';
import { PendoService } from 'analytics-lib';
import { Subject } from 'rxjs';

export enum MessageLevel {
  INFO = 'info',
  ERROR = 'error',
  SUCCESS = 'success',
}

export enum MessageAction {
  HIDE = 'hide',
  SHOW = 'show',
}

export interface IMessage {
  message: string;
  level: MessageLevel;
  details?: any;
  reference?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessageNotificationService {
  private static readonly PENDO_ERROR_EVENT_NAME = 'AIA/NoteTaker UI | Error Notification';
  private static readonly PENDO_UUID_PREFIX = 'pd-';

  private messageSubject = new Subject<{
    message: IMessage;
    action: MessageAction;
  }>();

  message$ = this.messageSubject.asObservable();

  private currentSuccessMessage?: IMessage;

  constructor(@Optional() private pendoService: PendoService | null = null) {}

  success(message: string, details?: any): IMessage {
    this.hideCurrentMessageIfPresent();

    const messageData: IMessage = {
      message,
      level: MessageLevel.SUCCESS,
      details,
    };

    this.messageSubject.next({ message: messageData, action: MessageAction.SHOW });
    this.currentSuccessMessage = messageData;

    return messageData;
  }

  info(message: string, details?: any): IMessage {
    this.hideCurrentMessageIfPresent();
    const messageData: IMessage = {
      message,
      level: MessageLevel.INFO,
      details,
    };
    this.messageSubject.next({ message: messageData, action: MessageAction.SHOW });
    this.currentSuccessMessage = messageData;

    return messageData;
  }

  error(message: string, error?: any, reference?: string): IMessage {
    const messageData: IMessage = {
      message,
      level: MessageLevel.ERROR,
      details: error,
      reference,
    };
    this.messageSubject.next({
      message: messageData,
      action: MessageAction.SHOW,
    });
    if (error) {
      console.error(error);
    }
    this.recordErrorInPendo(message, reference, error);
    return messageData;
  }

  hide(message: IMessage): void {
    this.messageSubject.next({ message, action: MessageAction.HIDE });
  }

  private hideCurrentMessageIfPresent(): void {
    if (this.currentSuccessMessage) {
      this.hide(this.currentSuccessMessage);
      this.currentSuccessMessage = undefined;
    }
  }

  private recordErrorInPendo(message: string, reference: string | undefined, error?: any): void {
    if (!this.pendoService) {
      return;
    }
    const id = reference ?? `${MessageNotificationService.PENDO_UUID_PREFIX}${crypto.randomUUID()}`;
    this.pendoService.recordEvent(MessageNotificationService.PENDO_ERROR_EVENT_NAME, {
      message: this.pendoService.truncateForPendo(message),
      id,
      error: this.pendoService.truncateForPendo(error),
    });
  }
}
